import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received. We'll be in touch.");
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <p className="eyebrow">Contact</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
            A direct line to <span className="italic" style={{ color: "var(--twr-bronze)" }}>the concierge.</span>
          </h1>
        </div>
      </section>

      <section className="section pt-12">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <InfoBlock Icon={MapPin} label="Visit" lines={["9000 Tavistock Lakes Blvd", "Lake Nona, Orlando, FL 32827"]} />
            <InfoBlock Icon={Phone} label="Call" lines={[<a key="phone" href="tel:+14075550100" className="hover:text-[var(--twr-bronze)] transition-colors">(407) 555-0100</a>]} />
            <InfoBlock Icon={Mail} label="Email" lines={[<a key="concierge" href="mailto:concierge@thewinroomfl.com" className="hover:text-[var(--twr-bronze)] transition-colors">concierge@thewinroomfl.com</a>, <a key="membership" href="mailto:membership@thewinroomfl.com" className="hover:text-[var(--twr-bronze)] transition-colors">membership@thewinroomfl.com</a>]} />
            <InfoBlock Icon={Clock} label="Hours" lines={["Mon — Thu · 11:30 — 23:00", "Fri — Sat · 11:30 — 00:00", "Sunday · 10:00 — 22:00"]} />
            <div className="surface p-4 mt-4">
              <p className="eyebrow mb-3">Parking</p>
              <p className="text-[var(--twr-warm-white)]/65 text-sm leading-relaxed">Complimentary valet for all guests. Member-reserved valet stalls at the south entrance.</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {!done ? (
              <form onSubmit={submit} className="surface p-8 sm:p-12" data-testid="contact-form">
                <h2 className="font-serif text-2xl">Send a message</h2>
                <div className="grid sm:grid-cols-2 gap-4 mt-8">
                  <Field label="Name" v={form.name} on={(v) => set("name", v)} required testId="contact-name" />
                  <Field label="Email" type="email" v={form.email} on={(v) => set("email", v)} required testId="contact-email" />
                  <Field label="Phone" v={form.phone} on={(v) => set("phone", v)} testId="contact-phone" />
                  <label className="block">
                    <span className="eyebrow">Subject</span>
                    <select value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-subject"
                      className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
                      {["General Inquiry", "Membership", "Reservations", "Private Events", "Press", "Careers"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                </div>
                <label className="block mt-5">
                  <span className="eyebrow">Message</span>
                  <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => set("message", e.target.value)}
                    className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-base" />
                </label>
                <button disabled={loading} data-testid="contact-submit" className="btn-bronze mt-8">{loading ? "Sending…" : "Send Message"}</button>
              </form>
            ) : (
              <div className="surface p-12 text-center" data-testid="contact-thankyou">
                <p className="eyebrow">Sent</p>
                <h2 className="font-serif text-3xl mt-4">Thank you for writing.</h2>
                <p className="text-[var(--twr-warm-white)]/70 mt-4">A member of our team will respond personally within twenty-four hours.</p>
              </div>
            )}

            <div className="surface mt-6 overflow-hidden">
              <iframe
                title="The Win Room — Lake Nona"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.123!2d-81.244!3d28.382!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0:0x0!2zMjjCsDIyJzU1LjIiTiA4McKwMTQnMzguNCJX!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%" height="360" style={{ border: 0, filter: "grayscale(70%) contrast(1.05) brightness(0.65)" }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ Icon, label, lines }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <Icon size={16} className="text-[var(--twr-bronze)]" strokeWidth={1.4} />
        <p className="eyebrow">{label}</p>
      </div>
      <div className="mt-3 space-y-1 text-[var(--twr-warm-white)]/75">
        {lines.map((l, i) => <p key={i} className="text-base leading-relaxed">{l}</p>)}
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}{required && " *"}</span>
      <input data-testid={testId} type={type} value={v} onChange={(e) => on(e.target.value)} required={required}
        className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm transition-colors" />
    </label>
  );
}
